import type { CausalProgressEvent, RunPhase, RunStatus } from './impact.types'

const PHASES = new Set<RunPhase>([
  'running_profile',
  'charging',
  'persisting',
  'completed',
  'failed',
])
const STATUSES = new Set<RunStatus>(['pending', 'running', 'completed', 'failed', 'cancelled'])

function recordOf(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function statusFor(value: unknown, phase: RunPhase): RunStatus {
  if (typeof value === 'string' && STATUSES.has(value as RunStatus)) return value as RunStatus
  if (phase === 'completed' || phase === 'failed') return phase
  return 'running'
}

/** Runtime validation keeps malformed shared-bus frames outside the Impact store. */
export function parseCausalProgressEvent(payload: unknown): CausalProgressEvent | null {
  const raw = recordOf(payload)
  if (!raw) return null
  const workspaceId = raw.workspace_id
  const executionId = raw.execution_id
  const phase = raw.phase
  if (
    (typeof workspaceId !== 'string' && typeof workspaceId !== 'number') ||
    (typeof executionId !== 'string' && typeof executionId !== 'number') ||
    typeof phase !== 'string' ||
    !PHASES.has(phase as RunPhase)
  ) {
    return null
  }

  const scheduleId = raw.schedule_id
  return {
    workspaceId: String(workspaceId),
    executionId: String(executionId),
    scheduleId: typeof scheduleId === 'number' && Number.isFinite(scheduleId) ? scheduleId : null,
    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : null,
    phase: phase as RunPhase,
    status: statusFor(raw.status, phase as RunPhase),
    errorMessage:
      typeof raw.error_message === 'string' && raw.error_message.trim() ? raw.error_message : null,
  }
}
